import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { personasAPI } from "@/lib/api";
import { Plus, Search, Edit, Trash2, Users, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function PersonasPage() {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadPersonas();
  }, []);

  const loadPersonas = async (searchTerm = "", tipo = "") => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (tipo && tipo !== "all") params.tipo = tipo;
      const response = await personasAPI.getAll(params);
      setPersonas(response.data);
    } catch (error) {
      toast.error("Error al cargar las personas");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPersonas(search, tipoFilter);
  };

  const handleFilterChange = (value) => {
    setTipoFilter(value);
    loadPersonas(search, value);
  };

  const handleDelete = async () => {
    try {
      await personasAPI.delete(deleteId);
      toast.success("Persona eliminada correctamente");
      loadPersonas();
    } catch (error) {
      toast.error("Error al eliminar la persona");
    } finally {
      setDeleteId(null);
    }
  };

  const getTipoBadgeColor = (tipo) => {
    switch (tipo) {
      case "clerigo":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "religioso":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-stone-100 text-stone-800 border-stone-200";
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case "clerigo":
        return "Clérigo";
      case "religioso":
        return "Religioso";
      default:
        return "Feligrés";
    }
  };

  return (
    <div className="space-y-6" data-testid="personas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
            Clérigos, Religiosos y Feligreses
          </h1>
          <p className="text-stone-600 mt-1">Gestión de miembros de la comunidad parroquial</p>
        </div>
        <Link to="/personas/nuevo">
          <Button className="bg-rose-950 hover:bg-rose-900 text-white" data-testid="nueva-persona-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Persona
          </Button>
        </Link>
      </div>

      {/* Search and Filter */}
      <Card className="border-stone-200 bg-white">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Buscar por nombre, apellido o cédula..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 bg-white border-stone-200"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={tipoFilter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-[180px] h-11">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por tipo" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="clerigo">Clérigos</SelectItem>
                  <SelectItem value="religioso">Religiosos</SelectItem>
                  <SelectItem value="feligres">Feligreses</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" variant="outline" className="h-11">
                Buscar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-stone-200 bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-stone-800">
            {personas.length} personas encontradas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-stone-400 animate-pulse">
              Cargando...
            </div>
          ) : personas.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No se encontraron personas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-ledger">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-6 py-4">Nombre</TableHead>
                    <TableHead className="px-6 py-4">Tipo</TableHead>
                    <TableHead className="px-6 py-4">Cédula</TableHead>
                    <TableHead className="px-6 py-4">Teléfono</TableHead>
                    <TableHead className="px-6 py-4 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {personas.map((persona) => (
                    <TableRow key={persona.id}>
                      <TableCell className="px-6 py-4 font-medium">
                        {persona.nombre} {persona.apellido}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <Badge variant="outline" className={getTipoBadgeColor(persona.tipo)}>
                          {getTipoLabel(persona.tipo)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {persona.cedula || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        {persona.telefono || "—"}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/personas/${persona.id}/editar`}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteId(persona.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta persona?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El registro será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
